import * as yup from 'yup';
import { emailField, optionalText, requiredText, TranslateFn } from '../../../library/validationRules';
import ResourcesIds from '../../../resources/ResourcesIds';

/**
 * What the emit-an-invoice form holds while it is being filled in.
 *
 * The amount is a string for the reason every number in this app's forms is: an `<input>`
 * hands back a string, and coercing per keystroke makes a field impossible to clear.
 *
 * The photograph is not here. A `File` is not a form value -- react-hook-form would have to
 * carry it through validation and reset, and Yup has nothing useful to say about it -- so the
 * dialog keeps it in its own state and blocks submit on it directly.
 */
export interface EmitInvoiceFormValues {
    clientName: string;
    clientCif: string;
    clientAddress: string;
    clientCity: string;
    clientState: string;
    clientEmail: string;
    receiptNumber: string;
    /** ISO date, as `<input type="date">` reads it. */
    receiptDate: string;
    receiptAmount: string;
}

/**
 * Deliberately thin, like `addPurchaseSchema` beside it. The server re-checks all of this and
 * owns the rules that matter -- the one-month window, the "only once" guard, what the amount
 * actually is -- so this exists to answer the therapist before they press the button rather
 * than to be the guard.
 *
 * Only the name is required of the client: an invoice to a private individual needs nothing
 * else, and demanding an address would make the commonest case the most work.
 */
export const createEmitInvoiceSchema = (translate: TranslateFn) =>
    yup.object({
        clientName: requiredText(translate, ResourcesIds.EMIT_INVOICE_CLIENT_NAME_REQUIRED),
        clientCif: optionalText(translate, 20),
        clientAddress: optionalText(translate, 255),
        clientCity: optionalText(translate, 100),
        clientState: optionalText(translate, 100),
        clientEmail: emailField(translate, { required: false }),
        receiptNumber: requiredText(translate, ResourcesIds.EMIT_INVOICE_RECEIPT_NUMBER_REQUIRED),
        receiptDate: yup
            .string()
            .required(translate(ResourcesIds.EMIT_INVOICE_RECEIPT_DATE_REQUIRED)),
        receiptAmount: yup
            .string()
            .required(translate(ResourcesIds.EMIT_INVOICE_RECEIPT_AMOUNT_REQUIRED))
            .test(
                'is-a-positive-amount',
                translate(ResourcesIds.EMIT_INVOICE_RECEIPT_AMOUNT_REQUIRED),
                (value?: string) => Number(value) >= 0 && value?.trim() !== ''
            ),
    });
